import React, { useContext, useEffect } from 'react';
import dashboardContext from '../../../context/dashboard/dashboardContext';
import DashboardLayout from '../shared/DashboardLayout';
import MetricCard from '../shared/MetricCard';
import RevenueChart from '../charts/RevenueChart';
import SalesByHourChart from '../charts/SalesByHourChart';
import TopItemsChart from '../charts/TopItemsChart';
import CostBreakdownChart from '../charts/CostBreakdownChart';
import StaffLeaderboard from '../charts/StaffLeaderboard';
import { formatIndianCurrency } from '../../../utils/currencyFormatter';
import './OwnerDashboard.css';

const OwnerDashboard = () => {
  const { dashboardData, loading, fetchDashboardData, selectedRestaurant } = useContext(dashboardContext);

  useEffect(() => {
    if (!dashboardData.summary) {
      fetchDashboardData(selectedRestaurant);
    }
  }, []);

  if (loading && !dashboardData.summary) {
    return (
      <DashboardLayout title="Dashboard" subtitle="Loading your restaurant insights...">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </DashboardLayout>
    );
  }

  const { 
    summary, 
    salesMetrics, 
    hourlySales, 
    topItems, 
    bottomItems,
    salesByChannel,
    costMetrics, 
    operationalMetrics,
    waiterStats,
    chefStats,
    customerMetrics,
    alerts
  } = dashboardData;

  return (
    <DashboardLayout 
      title="Restaurant Dashboard" 
      subtitle="Real-time insights and performance metrics"
    >
      {/* Alerts Section */}
      {alerts && alerts.length > 0 && (
        <div className="alerts-section">
          {alerts.map((alert, index) => (
            <div key={index} className={`alert alert-${alert.type}`}>
              <span className="alert-icon">
                {alert.type === 'success' && '✅'}
                {alert.type === 'warning' && '⚠️'}
                {alert.type === 'error' && '❌'}
                {alert.type === 'info' && 'ℹ️'}
              </span>
              <div className="alert-content">
                <strong>{alert.title}</strong>
                <p>{alert.message}</p>
              </div>
              <span className="alert-time">{alert.time}</span>
            </div>
          ))}
        </div>
      )}

      {/* Key Metrics Overview */}
      <div className="metrics-grid">
        <MetricCard
          title="Today's Revenue"
          value={summary?.todayRevenue || 0}
          unit="₹"
          change={parseFloat(summary?.revenueChange || 0)}
          changeLabel="vs yesterday"
          colorScheme="blue"
          target={summary?.revenueTarget}
        />
        
        <MetricCard
          title="Orders Today"
          value={summary?.todayOrders || 0}
          change={parseFloat(summary?.ordersChange || 0)}
          changeLabel="vs yesterday"
          colorScheme="green"
        />
        
        <MetricCard
          title="Avg Order Value"
          value={summary?.avgOrderValue || 0}
          unit="₹"
          colorScheme="purple"
        />
        
        <MetricCard
          title="Gross Profit Margin"
          value={summary?.grossProfitMargin || 0}
          unit="%"
          colorScheme="indigo"
        />
        
        <MetricCard
          title="Customer Satisfaction"
          value={customerMetrics?.customerSatisfactionScore || 0}
          unit="/5.0"
          colorScheme="yellow"
          subtitle={`Based on ${customerMetrics?.reviewsCount || 0} reviews`}
        />
        
        <MetricCard
          title="Table Turnover"
          value={operationalMetrics?.tableTurnoverRate || 0}
          unit="/day"
          colorScheme="green"
          subtitle="Tables per service period"
        />
      </div>

      {/* Revenue and Sales Section */}
      <div className="dashboard-section">
        <h2 className="section-title">📈 Sales Performance</h2>
        <div className="charts-grid-2">
          <RevenueChart data={salesMetrics || []} />
          <SalesByHourChart data={hourlySales || []} />
        </div>
      </div>

      {/* Top/Bottom Items */}
      <div className="dashboard-section">
        <h2 className="section-title">🍽️ Menu Performance</h2>
        <div className="charts-grid-2">
          <TopItemsChart data={topItems || []} title="Top Selling Items" />
          <TopItemsChart data={bottomItems || []} title="Underperforming Items" />
        </div>
        
        {/* Sales by Channel */}
        {salesByChannel && salesByChannel.length > 0 && (
          <div className="channel-cards">
            {salesByChannel.map((channel, index) => (
              <div key={index} className="channel-card">
                <div className="channel-icon">
                  {channel.channel === 'Dine-in' && '🍽️'}
                  {channel.channel === 'Takeout' && '🥡'}
                  {channel.channel === 'Delivery' && '🚗'}
                </div>
                <div className="channel-details">
                  <h4>{channel.channel}</h4>
                  <p className="channel-value">{formatIndianCurrency(channel.value, true, 2)}</p>
                  <p className="channel-percentage">{channel.percentage}% of total</p>
                  <p className={`channel-trend ${channel.trend >= 0 ? 'positive' : 'negative'}`}>
                    {channel.trend >= 0 ? '↑' : '↓'} {Math.abs(channel.trend)}% vs last period
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cost Analysis */}
      {costMetrics && (
        <div className="dashboard-section">
          <h2 className="section-title">💰 Cost & Profitability</h2>
          <CostBreakdownChart data={costMetrics} />
        </div>
      )}

      {/* Staff Performance */}
      <div className="dashboard-section">
        <h2 className="section-title">👥 Staff Performance</h2>
        <StaffLeaderboard 
          waiterStats={waiterStats || []} 
          chefStats={chefStats || []} 
        />
      </div>

      {/* Operational Metrics */}
      {operationalMetrics && (
        <div className="dashboard-section">
          <h2 className="section-title">⚙️ Operations</h2>
          <div className="operational-grid">
            <div className="operational-card">
              <div className="op-icon">⏱️</div>
              <div className="op-content">
                <h4>Avg Order Time</h4>
                <p className="op-value">{operationalMetrics.avgOrderFulfillmentTime} min</p>
                <p className="op-subtitle">Kitchen to table</p>
              </div>
            </div>
            
            <div className="operational-card">
              <div className="op-icon">🕐</div>
              <div className="op-content">
                <h4>Avg Wait Time</h4>
                <p className="op-value">{operationalMetrics.avgWaitTime} min</p>
                <p className="op-subtitle">Customer wait</p>
              </div>
            </div>
            
            <div className="operational-card">
              <div className="op-icon">✅</div>
              <div className="op-content">
                <h4>Order Accuracy</h4>
                <p className="op-value">{operationalMetrics.orderAccuracyRate}%</p>
                <p className="op-subtitle">Correct orders</p>
              </div>
            </div>
            
            <div className="operational-card">
              <div className="op-icon">🪑</div>
              <div className="op-content">
                <h4>Tables Occupied</h4>
                <p className="op-value">{operationalMetrics.tablesOccupied}/{operationalMetrics.totalTables}</p>
                <p className="op-subtitle">Current capacity</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Insights */}
      {customerMetrics && (
        <div className="dashboard-section">
          <h2 className="section-title">😊 Customer Insights</h2>
          <div className="customer-grid">
            <MetricCard
              title="Total Customers"
              value={customerMetrics.totalCustomers}
              subtitle={`${customerMetrics.newCustomers} new, ${customerMetrics.returningCustomers} returning`}
              colorScheme="blue"
              size="small"
            />
            
            <MetricCard
              title="Repeat Rate"
              value={customerMetrics.repeatRate}
              unit="%"
              colorScheme="green"
              size="small"
            />
            
            <MetricCard
              title="Avg Spend"
              value={customerMetrics.avgSpendPerCustomer}
              unit="₹"
              colorScheme="purple"
              size="small"
            />
            
            <MetricCard
              title="Net Promoter Score"
              value={customerMetrics.nps}
              colorScheme="indigo"
              size="small"
            />
          </div>
          
          <div className="feedback-summary">
            <div className="feedback-item">
              <span className="feedback-icon positive">👍</span>
              <div>
                <strong>Top Praise:</strong>
                <p>{customerMetrics.topPraise}</p>
              </div>
            </div>
            <div className="feedback-item">
              <span className="feedback-icon negative">👎</span>
              <div>
                <strong>Main Complaint:</strong>
                <p>{customerMetrics.topComplaint}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default OwnerDashboard;

